import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BellRing,
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  Download,
  FileText,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  Input,
  Modal,
  Select,
  Skeleton,
  toast,
} from '../../components/ui';
import { ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { formatDate, formatMoney } from '../../lib/format';
import {
  downloadInvoicePdfArchive,
  fetchInvoiceDrafts,
  useDeliverMany,
  useDeleteInvoices,
  useDeliveryStatus,
  useLogReminder,
  useRecordPayments,
  useSendInvoice,
  type BulkPaymentResult,
  type ContactChannel,
  type DeliveryResult,
  type InvoiceDraft,
  type InvoiceListItem,
} from './api';
import { SendReceiptField } from './send-receipt';
import { EnvironmentStrip, composerText, deliveryNote, paymentNote, plural } from './composer';
import { METHOD_LABELS, lastUsedMethod, rememberMethod, todayIso } from './payment-modal';
import { useCopyMe } from './recipients';

/** Every address one drafted message goes to — primary first, copies after. */
export function draftAudience(d: InvoiceDraft): string[] {
  return [...(d.to ? [d.to] : []), ...(d.cc ?? [])];
}

/** "ap@acme.com +2" — a row's worth. */
export function audienceLabel(d: InvoiceDraft): string {
  const all = draftAudience(d);
  if (all.length === 0) return 'no address';
  return all.length === 1 ? all[0] : `${all[0]} +${all.length - 1}`;
}

// ---------------------------------------------------------------------------
// Working the queue in ONE action
//
// This file used to walk a selection through the composer one screen at a
// time: "Chase #1156 · 1 of 15", then Next, fifteen times, each screen asking
// the operator to open a mail app, paste, download the PDF, find it, attach it
// and come back. Roughly ninety interactions for a job Harvest does with a
// checkbox and a button — and it did not even guard the selection: from the
// default Open tab it queued a dunning email at an invoice not due for three
// weeks.
//
// Both problems are fixed in the same place. The server drafts every message,
// classifies every row (ready · not due yet · nothing to send to) and returns
// the list; the dialog shows exactly what will go where; one button sends them
// all, each with its own PDF and its own delivery record.
// ---------------------------------------------------------------------------

/**
 * ⚠ **THE RAIL COVERS TWO REASONS NOW, AND THE COPY MUST NAME THE RIGHT ONE.**
 *
 * A row is excluded because the money is not late yet, OR because it was
 * chased inside the cadence and the next follow-up has not arrived — the
 * second half used to be missing entirely, so the dialog listed #1053 (chased
 * 3×, last 4 days ago, next follow-up Aug 16) under "Ready to send" while the
 * list row behind it printed "Chased 3× · last 4d ago". The server decides,
 * words the sentence and hands over `cautionCode`; this only picks the blurb.
 */
function cautionBlurb(rows: InvoiceDraft[], surface: 'send' | 'copy' = 'send'): string {
  const early = rows.some((r) => r.cautionCode === 'not-due');
  const recent = rows.some((r) => r.cautionCode === 'chased-recently');
  const add =
    surface === 'copy'
      ? 'Tick to add a courtesy note for them to the block.'
      : 'Tick to send them a courtesy note anyway.';
  const why =
    early && recent
      ? 'Left alone, nothing goes to a client whose money is not late, or who was chased inside the cadence.'
      : recent
        ? 'Left alone, nothing goes to a client who was already chased inside the cadence — the same notice twice in a week is what the cadence exists to prevent.'
        : 'Left alone, nothing goes to a client whose money is not late.';
  return `${add} ${why}`;
}

/** "#1053 Harbor & Main Bank · chased 4d ago · next Aug 16, 2026" */
function cautionRow(d: InvoiceDraft): string {
  return `#${d.number} ${d.clientName} · ${d.cautionSummary ?? `due ${formatDate(d.dueDate)}`}`;
}

/** States a delivery can be recorded against. PAID/VOID are neither. */
export function isContactable(r: InvoiceListItem): boolean {
  return r.state === 'DRAFT' || r.state === 'OPEN' || r.state === 'PARTIAL';
}

// ---------------------------------------------------------------------------
// Checkbox — the control the whole feature hangs off
// ---------------------------------------------------------------------------

export function RowCheckbox({
  checked,
  indeterminate = false,
  onToggle,
  label,
  className,
  disabled = false,
}: {
  checked: boolean;
  indeterminate?: boolean;
  /** `shiftKey` is true when the user shift-clicked, for range selection. */
  onToggle: (checked: boolean, shiftKey: boolean) => void;
  label: string;
  className?: string;
  /** A row that cannot take the action; `label` says why. */
  disabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={label}
      title={label}
      // The toggle rides `click`, not `change`, because that is the event that
      // carries shiftKey — and keyboard Space fires a click too, so range
      // selection and plain selection both work without a mouse.
      onClick={(e) => {
        // The row underneath navigates; ticking a box must not also open it.
        e.stopPropagation();
        onToggle(!checked, e.shiftKey);
      }}
      onChange={() => {
        /* controlled by `checked`; the click handler above is the toggle */
      }}
      className={cn(
        'h-4 w-4 shrink-0 cursor-pointer rounded border-grey-300 accent-accent disabled:cursor-not-allowed disabled:opacity-40',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
        className,
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// The send dialog — the whole selection on one screen
// ---------------------------------------------------------------------------

function DispositionPill({ result }: { result: DeliveryResult }) {
  const map: Record<DeliveryResult['disposition'], { label: string; className: string }> = {
    sent: { label: 'Sent', className: 'bg-status-paid/15 text-status-paid' },
    filed: { label: 'Filed', className: 'bg-accent/15 text-accent-700' },
    skipped: { label: 'Skipped', className: 'bg-grey-100 text-grey-500' },
    refused: { label: 'Refused', className: 'bg-status-late-bg text-status-late' },
    failed: { label: 'Failed', className: 'bg-status-late-bg text-status-late' },
  };
  const pill = map[result.disposition];
  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-2 py-0.5 text-caption font-bold uppercase tracking-wider',
        pill.className,
      )}
    >
      {pill.label}
    </span>
  );
}

function BulkSendDialog({
  ids,
  onClose,
  onDone,
}: {
  ids: string[];
  onClose: () => void;
  /** Fired once anything has actually gone, so the list can clear its ticks. */
  onDone: () => void;
}) {
  const [drafts, setDrafts] = useState<InvoiceDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeNotDue, setIncludeNotDue] = useState(false);
  const [note, setNote] = useState('');
  const [report, setReport] = useState<DeliveryResult[] | null>(null);
  const [copyMe, setCopyMe] = useCopyMe();
  const status = useDeliveryStatus();
  const deliver = useDeliverMany();

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchInvoiceDrafts(ids)
      .then((rows) => {
        if (!cancelled) setDrafts(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not draft the messages');
      });
    return () => {
      cancelled = true;
    };
  }, [ids]);

  const groups = useMemo(() => {
    const rows = drafts ?? [];
    return {
      ready: rows.filter((r) => !r.blocked && !r.caution),
      notDue: rows.filter((r) => !r.blocked && r.caution),
      blocked: rows.filter((r) => r.blocked),
    };
  }, [drafts]);

  const willSend = includeNotDue ? [...groups.ready, ...groups.notDue] : groups.ready;
  const sendCount = willSend.length;
  const sendTotal = willSend.reduce((s, r) => s + r.dueAmount, 0);
  const modeNote = deliveryNote(status.data);
  // ⚠ "the 'Pay now' button on THIS INVOICE" headed a nine-invoice run. The
  // sentence now counts what the dialog is actually about.
  const payNote = paymentNote(status.data, ids.length === 1 ? 'one' : 'many');
  const transmitting = status.data?.mode !== 'file';

  /**
   * ⚠ ONE CLIENT, ONE MESSAGE. A client owing three invoices used to get three
   * dunning notes in the same minute, each demanding a different figure and
   * none of them stating the total — while the invoice detail screen had warned
   * about exactly that for months without any way to act on it. Chases for one
   * client now leave as ONE statement with every PDF attached; each invoice
   * still writes its own delivery record. Only chases group: two NEW invoices
   * are two events with two sets of terms.
   */
  const messages = useMemo(() => {
    const seen = new Set<string>();
    let count = 0;
    let grouped = 0;
    const perClient = new Map<string, number>();
    for (const d of willSend) {
      if (d.mode !== 'remind') {
        count += 1;
        continue;
      }
      perClient.set(d.clientId, (perClient.get(d.clientId) ?? 0) + 1);
      if (!seen.has(d.clientId)) {
        seen.add(d.clientId);
        count += 1;
      }
    }
    for (const [, n] of perClient) if (n > 1) grouped += 1;
    return { count, grouped, perClient };
  }, [willSend]);

  const run = () => {
    deliver.mutate(
      { ids: willSend.map((r) => r.id), note: note.trim() || null, includeNotDue, copyMe },
      {
        onSuccess: (res) => {
          setReport(res.results);
          onDone();
          const verb = res.mode === 'file' ? 'filed' : 'sent';
          if (res.sent + res.filed > 0) {
            toast.success(
              `${plural(res.sent + res.filed, 'invoice', 'invoices')} ${verb} in ${plural(
                res.messages ?? res.sent + res.filed,
                'message',
                'messages',
              )}${res.failed > 0 ? ` · ${res.failed} failed` : ''} — every PDF attached, one delivery record each`,
            );
          }
          if (res.failed > 0 && res.sent + res.filed === 0) {
            toast.error(`Nothing went out — ${plural(res.failed, 'invoice', 'invoices')} failed`);
          }
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not send'),
      },
    );
  };

  // ---- the result screen ---------------------------------------------------
  if (report) {
    const ok = report.filter((r) => r.disposition === 'sent' || r.disposition === 'filed');
    return (
      <Modal
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        size="lg"
        className="max-h-[92vh]"
        title={`${plural(ok.length, 'message', 'messages')} away`}
        description={
          transmitting
            ? 'Each one carried its own invoice PDF and wrote its own delivery record.'
            : `Written to ${status.data?.fileDir ?? '.mail'}/ — nothing was transmitted.`
        }
        footer={
          <div className="flex w-full justify-end border-t border-grey-100 pt-4">
            <Button onClick={onClose}>Done</Button>
          </div>
        }
      >
        <ul className="-mx-1 max-h-[56vh] divide-y divide-grey-100 overflow-y-auto px-1 text-body-sm">
          {report.map((r) => (
            <li key={r.id} className="flex items-start justify-between gap-3 py-2">
              <span className="min-w-0">
                <Link
                  to={`/invoices/${r.id}`}
                  className="num font-semibold text-accent-700 hover:underline"
                >
                  #{r.number}
                </Link>{' '}
                <span className="text-secondary">{r.clientName}</span>
                <span className="block truncate text-caption text-grey-500">
                  {r.reason ??
                    `${
                      r.recipients?.length > 1
                        ? `${r.recipients[0]} +${r.recipients.length - 1}`
                        : (r.recipients?.[0] ?? r.to)
                    } · ${r.attachment ?? 'no attachment'}`}
                </span>
                {(r.groupedWith?.length ?? 0) > 0 && (
                  <span className="block text-caption text-accent-700">
                    Sent as one message with {r.groupedWith.map((n) => `#${n}`).join(', ')}
                  </span>
                )}
                {(r.learned?.length ?? 0) > 0 && (
                  <span className="block text-caption text-grey-500">
                    {r.learned.join(', ')} saved to {r.clientName}
                  </span>
                )}
              </span>
              <DispositionPill result={r} />
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  // ---- the confirm screen --------------------------------------------------
  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="lg"
      className="max-h-[92vh]"
      title={`Send ${messages.count === 1 ? '1 message' : `${messages.count} messages`}`}
      description={
        drafts
          ? `${formatMoney(sendTotal, { whole: true })} across ${plural(
              sendCount,
              'invoice',
              'invoices',
            )}${
              messages.grouped > 0
                ? ` · ${plural(messages.grouped, 'client gets', 'clients get')} ONE message covering everything they owe`
                : ''
            } · every PDF attached`
          : 'Drafting every message…'
      }
      footer={
        <div className="flex w-full flex-col gap-2 border-t border-grey-100 pt-4 sm:flex-row sm:items-center sm:justify-end">
          <Button variant="ghost" className="sm:mr-auto" onClick={onClose} disabled={deliver.isPending}>
            Cancel
          </Button>
          <Button
            onClick={run}
            disabled={sendCount === 0 || !drafts}
            loading={deliver.isPending}
            title={
              sendCount === 0
                ? 'Nothing in this selection can be sent'
                : `Emails ${sendCount} client${sendCount === 1 ? '' : 's'} now`
            }
          >
            <Send className="h-4 w-4" aria-hidden />
            {messages.count === 1 ? 'Send it' : `Send all ${messages.count}`}
          </Button>
        </div>
      }
    >
      <div className="-mx-1 max-h-[58vh] space-y-4 overflow-y-auto px-1">
        {error && (
          <p className="rounded-lg border border-status-late-border bg-status-late-bg px-3 py-2.5 text-body-sm text-status-late">
            {error}
          </p>
        )}
        {!drafts && !error && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {/* Two development facts about the BOX, one quiet strip — they were
            two full-width warnings above the only decision in the dialog. */}
        <EnvironmentStrip facts={[modeNote, payNote]} />

        {/* ⚠ THE ONLY DECISION IN THIS DIALOG COMES FIRST. It used to sit
            below a nine-row list and two full-width banners — the reader had to
            scroll past everything that needs no thought to reach the one thing
            that does. */}
        {/* ⚠ THE GUARD. A selection made on the Open tab contains invoices that
            are not due yet; dunning one of them is a phone call from the client
            the next morning. They are listed, excluded, and turned on
            deliberately — never queued by accident. */}
        {drafts && groups.notDue.length > 0 && (
          <section
            className={cn(
              'rounded-lg border px-3 py-2.5',
              includeNotDue ? 'border-accent/40 bg-accent/5' : 'border-grey-200 bg-grey-50',
            )}
          >
            <label className="flex cursor-pointer items-start gap-2.5">
              <input
                type="checkbox"
                checked={includeNotDue}
                onChange={(e) => setIncludeNotDue(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-grey-300 accent-accent"
              />
              <span className="min-w-0">
                <span className="block text-body-sm font-semibold text-secondary">
                  {plural(groups.notDue.length, 'invoice is', 'invoices are')} not due a chase yet
                  — excluded
                </span>
                <span className="block text-caption text-grey-600">
                  {cautionBlurb(groups.notDue)}
                </span>
                <span className="mt-1 block text-caption text-grey-500">
                  {groups.notDue.map(cautionRow).join(' · ')}
                </span>
              </span>
            </label>
          </section>
        )}

        {drafts && groups.ready.length > 0 && (
          <section>
            <p className="mb-1.5 text-caption font-bold uppercase tracking-wider text-grey-500">
              Ready to send · {groups.ready.length}
            </p>
            <ul className="divide-y divide-grey-100 rounded-lg border border-grey-200">
              {groups.ready.map((d) => (
                <li key={d.id} className="flex items-start justify-between gap-3 px-3 py-2">
                  <span className="min-w-0">
                    <span className="num font-semibold text-secondary">#{d.number}</span>{' '}
                    <span className="text-body-sm text-secondary">{d.clientName}</span>
                    {d.mode === 'remind' && (messages.perClient.get(d.clientId) ?? 0) > 1 && (
                      <span
                        title={`${d.clientName} owes ${messages.perClient.get(d.clientId)} invoices — one message covers them all`}
                        className="ml-1.5 rounded-full bg-accent/15 px-1.5 py-0.5 text-caption font-bold uppercase tracking-wider text-accent-700"
                      >
                        1 of {messages.perClient.get(d.clientId)} in one message
                      </span>
                    )}
                    <span
                      className="block truncate text-caption text-grey-500"
                      title={draftAudience(d).join(', ')}
                    >
                      {d.mode === 'send' ? 'Issue & send' : 'Chase'} · {audienceLabel(d)}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span
                      className={cn(
                        'num block text-body-sm font-bold',
                        d.isLate ? 'text-status-late' : 'text-secondary',
                      )}
                    >
                      {formatMoney(d.dueAmount)}
                    </span>
                    <span className="num block text-caption text-grey-500">
                      {d.isLate ? `${d.daysOverdue}d overdue` : `due ${formatDate(d.dueDate)}`}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {drafts && groups.blocked.length > 0 && (
          <section>
            <p className="mb-1.5 text-caption font-bold uppercase tracking-wider text-grey-500">
              Nothing to send · {groups.blocked.length}
            </p>
            <ul className="space-y-1 text-caption text-grey-500">
              {groups.blocked.map((d) => (
                <li key={d.id}>
                  <span className="num font-semibold text-secondary">#{d.number}</span>{' '}
                  {d.clientName} — {d.blocked}
                </li>
              ))}
            </ul>
          </section>
        )}

        {drafts && sendCount > 0 && (
          <>
            {/* One toggle, remembered across runs — the same habit the composer
                carries, so "always keep a copy" is a decision made once. */}
            {willSend[0]?.copyTo && (
              <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-grey-200 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={copyMe}
                  onChange={(e) => setCopyMe(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-grey-300 accent-accent"
                />
                <span className="min-w-0">
                  <span className="block text-body-sm font-semibold text-secondary">
                    Send a copy of every message to me
                  </span>
                  <span className="num block text-caption text-grey-600">
                    {willSend[0].copyTo} · blind-copied, so no client sees it
                  </span>
                </span>
              </label>
            )}
            <Input
              label="Internal note (optional)"
              placeholder="Month-end chase run"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              hint="Recorded against every delivery in this run. Never shown to a client."
            />
            {/* Proof, not a promise: the exact text the first message carries. */}
            <details className="rounded-lg border border-grey-200 bg-grey-50 px-3 py-2">
              <summary className="cursor-pointer text-body-sm font-semibold text-secondary">
                Read the message going to {willSend[0].clientName}
              </summary>
              <p className="num mt-2 whitespace-pre-wrap text-caption leading-relaxed text-grey-700">
                {composerText(willSend[0].subject, willSend[0].body)}
              </p>
            </details>
          </>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Copy all — the case where the drafted bodies need no editing at all
// ---------------------------------------------------------------------------

/**
 * ⚠ THE NOT-DUE GUARD APPLIES HERE TOO.
 *
 * "Chase all" narrows a selection of 15 to the 7 that are actually late and
 * explains the exclusion — *"Left alone, nothing goes to a client whose money
 * is not late"*. "Copy all as text" sat right beside it and quietly produced
 * all 15, including eight invoices due weeks from now. The wording adapts, so
 * nothing rude reached a clipboard, but the clipboard is the path an owner uses
 * when they send from their own inbox: it is the LESS supervised of the two,
 * not the more. One rail, both buttons — and the tick that overrides it is the
 * same tick, in the same words.
 */
interface CopyBundle {
  ready: InvoiceDraft[];
  notDue: InvoiceDraft[];
  copied: boolean;
}

function bundleText(rows: InvoiceDraft[]): string {
  return rows
    .map((d) => {
      const all = draftAudience(d);
      return [
        `To: ${all[0] ?? '(no billing email on file)'}`,
        // The whole audience, so pasting into an inbox reproduces the send —
        // a bundle that dropped the copy list would put the manual forward
        // straight back.
        all.length > 1 ? `Cc: ${all.slice(1).join(', ')}` : null,
        composerText(d.subject, d.body),
      ]
        .filter(Boolean)
        .join('\n');
    })
    .join(`\n\n${'—'.repeat(48)}\n\n`);
}

function CopyAllDialog({
  bundle,
  onClose,
  onRecord,
  recording,
}: {
  bundle: CopyBundle;
  onClose: () => void;
  onRecord: (rows: InvoiceDraft[]) => void;
  recording: boolean;
}) {
  const [includeNotDue, setIncludeNotDue] = useState(false);
  const rows = includeNotDue ? [...bundle.ready, ...bundle.notDue] : bundle.ready;
  const text = useMemo(() => bundleText(rows), [rows]);
  const [copied, setCopied] = useState(bundle.copied);
  // A changed set is a different clipboard: the tick must not leave "Copied"
  // standing over text nobody has copied.
  useEffect(() => setCopied(bundle.copied && !includeNotDue), [includeNotDue, bundle.copied]);
  const drafts = rows.filter((r) => r.mode === 'send');
  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="lg"
      className="max-h-[92vh]"
      title={`${plural(rows.length, 'message', 'messages')} ready to paste`}
      description="One block per invoice, addressed and subject-lined, in the order they are listed."
      footer={
        <div className="flex w-full flex-col gap-2 border-t border-grey-100 pt-4 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            className="sm:mr-auto"
            disabled={rows.length === 0}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(text);
                setCopied(true);
                toast.success('Copied to the clipboard');
              } catch {
                toast.error('Could not copy — select the text above and copy manually');
              }
            }}
          >
            {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            {copied ? 'Copied' : 'Copy to clipboard'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={recording}>
            Close without recording
          </Button>
          <Button onClick={() => onRecord(rows)} loading={recording} disabled={rows.length === 0}>
            <Send className="h-4 w-4" aria-hidden />
            Record {rows.length} deliveries
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {/* The same rail the send path advertises, in the same words — see the
            note on CopyBundle. */}
        {bundle.notDue.length > 0 && (
          <section
            className={cn(
              'rounded-lg border px-3 py-2.5',
              includeNotDue ? 'border-accent/40 bg-accent/5' : 'border-grey-200 bg-grey-50',
            )}
          >
            <label className="flex cursor-pointer items-start gap-2.5">
              <input
                type="checkbox"
                checked={includeNotDue}
                onChange={(e) => setIncludeNotDue(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-grey-300 accent-accent"
              />
              <span className="min-w-0">
                <span className="block text-body-sm font-semibold text-secondary">
                  {plural(bundle.notDue.length, 'invoice is', 'invoices are')} not due a chase yet
                  — excluded
                </span>
                <span className="block text-caption text-grey-600">
                  {cautionBlurb(bundle.notDue, 'copy')}
                </span>
                <span className="mt-1 block text-caption text-grey-500">
                  {bundle.notDue.map(cautionRow).join(' · ')}
                </span>
              </span>
            </label>
          </section>
        )}
        {rows.length === 0 && (
          <p className="rounded-lg border border-grey-200 bg-grey-50 px-3 py-2 text-body-sm text-grey-600">
            Nothing in this selection is late. Tick the box above to draft courtesy notes anyway.
          </p>
        )}
        <p className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-body-sm text-secondary">
          Recording marks each of these as delivered — channel, address, timestamp and subject —
          against its invoice.
          {drafts.length > 0 && (
            <>
              {' '}
              <span className="font-semibold">
                {plural(drafts.length, 'draft', 'drafts')} ({drafts.map((d) => `#${d.number}`).join(', ')})
                {drafts.length === 1 ? ' is' : ' are'} issued by this
              </span>{' '}
              — draft to open, with payment falling due on their terms.
            </>
          )}
        </p>
        <textarea
          readOnly
          value={text}
          rows={12}
          onFocus={(e) => e.currentTarget.select()}
          className="num w-full rounded-lg border border-grey-200 bg-grey-50 p-3 text-caption leading-relaxed text-grey-700 focus:outline-none focus:ring-2 focus:ring-accent/30"
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// The action bar — what replaces the chase chips once rows are ticked
// ---------------------------------------------------------------------------

export function BulkActionBar({
  selected,
  totalInView,
  onClear,
  onExportCsv,
}: {
  /** The selected rows, in the order they appear on screen. */
  selected: InvoiceListItem[];
  totalInView: number;
  onClear: () => void;
  /** Exports exactly these rows through the page's own CSV builder. */
  onExportCsv: (rows: InvoiceListItem[]) => void;
}) {
  const send = useSendInvoice();
  const logReminder = useLogReminder();
  const status = useDeliveryStatus();
  /**
   * The worklist is FROZEN when the dialog opens. Sending invoice 1 of 3
   * invalidates the list, which re-filters it out of the current view — a live
   * `selected` would shrink the run underneath itself.
   */
  const [sendQueue, setSendQueue] = useState<string[] | null>(null);
  const [confirmMarkSent, setConfirmMarkSent] = useState(false);
  const [pdfProgress, setPdfProgress] = useState<number | null>(null);
  const [bundle, setBundle] = useState<CopyBundle | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [deleteQueue, setDeleteQueue] = useState<InvoiceListItem[] | null>(null);
  const [payQueue, setPayQueue] = useState<InvoiceListItem[] | null>(null);

  const drafts = useMemo(() => selected.filter((r) => r.state === 'DRAFT'), [selected]);
  /** Sent and still owed — the only invoices a payment can land on. */
  const payable = useMemo(
    () => selected.filter((r) => (r.state === 'OPEN' || r.state === 'PARTIAL') && r.dueAmount > 0.004),
    [selected],
  );
  /** Drafts this app owns — a Harvest-mirrored draft is deleted in Harvest. */
  const deletable = useMemo(() => drafts.filter((r) => !r.sourceOwned), [drafts]);
  const contactable = useMemo(() => selected.filter(isContactable), [selected]);
  const settled = selected.length - contactable.length;
  /**
   * ⚠ **A DRAFT IS NOT DUE, SO IT IS NOT IN THE FIGURE LABELLED "due".**
   *
   * This bar used to foot every ticked row's `dueAmount` and call the result
   * due: ticking all 156 printed **$235,277 due** eight inches under an
   * Outstanding tile reading **$205,834** — the gap being exactly the two
   * drafts ($29,443.50), which nobody has been sent and nobody owes. The
   * table's own `<tfoot>` had already learned to say so; the selection bar,
   * which this repo's rules name specifically, had not.
   *
   * `OPEN | PARTIAL` is `RECEIVABLE_STATES` — the same population
   * `/api/invoices/summary` counts — so a full selection now foots to the tile
   * to the dollar. The drafts are not hidden, they are priced separately in the
   * same breath.
   */
  const dueTotal = selected.reduce(
    (s, r) => s + (r.state === 'OPEN' || r.state === 'PARTIAL' ? r.dueAmount : 0),
    0,
  );
  const draftDue = drafts.reduce((s, r) => s + r.dueAmount, 0);
  const amountTotal = selected.reduce((s, r) => s + r.amount, 0);

  const sendLabel =
    drafts.length === 0
      ? `Chase all (${contactable.length})`
      : drafts.length === contactable.length
        ? `Send all (${contactable.length})`
        : `Send & chase (${contactable.length})`;

  const copyAll = async () => {
    setPreparing(true);
    try {
      const rows = await fetchInvoiceDrafts(contactable.map((r) => r.id));
      // The guard is applied BEFORE the clipboard write, not after it: by the
      // time the dialog opens the not-due messages are already out of the
      // buffer the user pasted from.
      const ready = rows.filter((r) => !r.caution);
      const notDue = rows.filter((r) => r.caution);
      let copied = false;
      if (ready.length > 0) {
        try {
          await navigator.clipboard.writeText(bundleText(ready));
          copied = true;
        } catch {
          /* shown in the dialog instead — the text is right there to copy */
        }
      }
      setBundle({ ready, notDue, copied });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not build the messages');
    } finally {
      setPreparing(false);
    }
  };

  /**
   * Record a delivery this app did not perform — the copied bundle, or the post
   * run. Same endpoints as a single invoice, in a loop.
   */
  const recordAll = async (rows: InvoiceDraft[], channel: ContactChannel) => {
    setRecording(true);
    let ok = 0;
    const failed: string[] = [];
    for (const d of rows) {
      try {
        if (d.mode === 'send') {
          await send.mutateAsync({
            id: d.id,
            channel,
            to: d.to,
            subject: d.subject,
            issueDate: d.issueDate,
          });
        } else {
          await logReminder.mutateAsync({ id: d.id, channel, to: d.to, subject: d.subject });
        }
        ok += 1;
      } catch {
        failed.push(`#${d.number}`);
      }
    }
    setRecording(false);
    setBundle(null);
    setConfirmMarkSent(false);
    onClear();
    if (ok > 0) {
      toast.success(
        `${plural(ok, 'delivery', 'deliveries')} recorded${
          failed.length > 0 ? ` · ${failed.length} failed` : ''
        }`,
      );
    }
    if (failed.length > 0) {
      toast.error(`Could not record ${failed.join(', ')} — open them individually`);
    }
  };

  /**
   * ⚠ ONE FILE, ONE SAVE. This used to loop a per-invoice download, and real
   * Chrome raises an "Allow multiple downloads?" permission interstitial at the
   * third file — so the month-end batch this button exists for either
   * half-completed or made the owner grant a permission that reads like
   * malware. The server zips the selection; the browser saves once.
   */
  const downloadPdfs = async () => {
    setPdfProgress(0);
    try {
      const { fileName, included, skipped } = await downloadInvoicePdfArchive(
        selected.map((r) => r.id),
      );
      if (skipped > 0) {
        // Said, never silently dropped: "I got 14 of 15" must not be something
        // the owner discovers by counting files.
        toast.error(
          `${included} of ${included + skipped} PDFs are in ${fileName} — ${skipped} failed to build`,
        );
      } else {
        toast.success(`${plural(included, 'PDF', 'PDFs')} in ${fileName}`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not build the archive');
    } finally {
      setPdfProgress(null);
    }
  };

  return (
    <>
      <div className="sticky top-16 z-20 mb-3 rounded-xl bg-secondary px-3 py-2.5 text-white shadow-elevated">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="num flex items-center gap-2 text-body-sm font-bold">
            <span>
              {selected.length} selected
              {selected.length === totalInView && totalInView > 1 && (
                <span className="font-medium text-white/60"> · all in this view</span>
              )}
              <span className="ml-2 font-medium text-white/80">
                {/* A total across a set is whole dollars, like every other
                    rollup on this page — see ROLLUP in InvoicesListPage. */}
                {dueTotal > 0
                  ? `${formatMoney(dueTotal, { whole: true })} due`
                  : draftDue > 0
                    ? `${formatMoney(draftDue, { whole: true })} drafted`
                    : `${formatMoney(amountTotal, { whole: true })} total`}
              </span>
              {/* Named, never folded in: the same reconciliation the table's
                  totals row prints, so the bar and the Outstanding tile above
                  it cannot contradict each other. */}
              {dueTotal > 0 && draftDue > 0 && (
                <span
                  className="ml-2 font-medium text-white/60"
                  title={`${formatMoney(draftDue)} across ${drafts.length} draft${
                    drafts.length === 1 ? '' : 's'
                  } — a draft has not been sent, so nobody owes it yet`}
                >
                  {`+ ${formatMoney(draftDue, { whole: true })} in ${drafts.length} draft${
                    drafts.length === 1 ? '' : 's'
                  }, not yet due`}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={onClear}
              title="Clear the selection"
              className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-caption font-semibold text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              Clear
            </button>
          </p>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-1.5">
            <Button
              size="sm"
              variant="accent"
              disabled={contactable.length === 0}
              onClick={() => setSendQueue(contactable.map((r) => r.id))}
              title={
                contactable.length === 0
                  ? 'Nothing in this selection can take a delivery'
                  : `Email all ${contactable.length} now — each with its own PDF attached and its own delivery record`
              }
            >
              {drafts.length === contactable.length ? (
                <Send className="h-4 w-4" aria-hidden />
              ) : (
                <BellRing className="h-4 w-4" aria-hidden />
              )}
              {sendLabel}
            </Button>
            <Button
              size="sm"
              variant="outline"
              loading={preparing}
              disabled={contactable.length === 0}
              onClick={() => void copyAll()}
              title="Draft every message now and copy them as one block — for an inbox we cannot reach"
            >
              <Copy className="h-4 w-4" aria-hidden />
              Copy all as text
            </Button>
            {payable.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPayQueue(payable)}
                title={`Record ${plural(payable.length, 'invoice', 'invoices')} as paid in full — a month of deposits at once`}
              >
                <CreditCard className="h-4 w-4" aria-hidden />
                Record payment ({payable.length})
              </Button>
            )}
            {drafts.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmMarkSent(true)}
                title={`Issue ${drafts.length} draft${drafts.length === 1 ? '' : 's'} without writing an email — posted, or sent from your own inbox`}
              >
                <CheckCircle2 className="h-4 w-4" aria-hidden />
                Mark as sent ({drafts.length})
              </Button>
            )}
            {deletable.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDeleteQueue(deletable)}
                title={`Delete ${plural(deletable.length, 'draft', 'drafts')} permanently — issued invoices are never deleted`}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete drafts ({deletable.length})
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              loading={pdfProgress !== null}
              onClick={() => void downloadPdfs()}
              title={`Save all ${selected.length} as one zip — one file, one save dialog`}
            >
              <Download className="h-4 w-4" aria-hidden />
              {pdfProgress !== null
                ? `Zipping ${selected.length}…`
                : `Download PDFs (${selected.length})`}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onExportCsv(selected)}
              title="Export just these rows, with their chase state"
            >
              <FileText className="h-4 w-4" aria-hidden />
              Export CSV
            </Button>
          </div>
        </div>
        {settled > 0 && (
          <p className="mt-1.5 text-caption text-white/70">
            {plural(settled, 'selected invoice is', 'selected invoices are')} paid or void — they are
            downloaded and exported, but nothing is sent or chased on them.
          </p>
        )}
        {status.data?.writesToDisk && (
          <p className="mt-1.5 text-caption text-white/70">
            Email is in file mode — messages and their PDFs are written to{' '}
            <span className="num">{status.data.fileDir}/</span>, not transmitted.
          </p>
        )}
        {/* Said once, where it is needed — a queue you can work without the
            mouse is only useful if you know you can. */}
        <p className="mt-1.5 hidden text-caption text-white/50 sm:block">
          Shift-click a row for a range · <span className="num">j</span> /{' '}
          <span className="num">k</span> move · <span className="num">x</span> ticks the focused row
          · <span className="num">a</span> all · <span className="num">Esc</span> clears
        </p>
      </div>

      {sendQueue && (
        <BulkSendDialog
          key={sendQueue.join(',')}
          ids={sendQueue}
          onClose={() => setSendQueue(null)}
          onDone={onClear}
        />
      )}

      {deleteQueue && (
        <DeleteDraftsDialog
          key={deleteQueue.map((r) => r.id).join(',')}
          rows={deleteQueue}
          skipped={selected.length - deleteQueue.length}
          onClose={() => setDeleteQueue(null)}
          onDeleted={() => {
            setDeleteQueue(null);
            onClear();
          }}
        />
      )}

      {payQueue && (
        <BulkPaymentDialog
          key={payQueue.map((r) => r.id).join(',')}
          rows={payQueue}
          left={selected.filter((r) => !payQueue.some((p) => p.id === r.id))}
          onClose={() => setPayQueue(null)}
          onDone={() => {
            setPayQueue(null);
            onClear();
          }}
        />
      )}

      {bundle && (
        <CopyAllDialog
          bundle={bundle}
          recording={recording}
          onClose={() => setBundle(null)}
          onRecord={(rows) => void recordAll(rows, 'copied')}
        />
      )}

      <ConfirmDialog
        open={confirmMarkSent}
        onOpenChange={setConfirmMarkSent}
        title={`Issue ${plural(drafts.length, 'draft', 'drafts')} without sending?`}
        description={
          <>
            {drafts.map((d) => `#${d.number}`).join(', ')} —{' '}
            <span className="num font-semibold text-secondary">
              {formatMoney(drafts.reduce((s, d) => s + d.amount, 0), { whole: true })}
            </span>{' '}
            — move from draft to open, dated today, each with a delivery recorded as “logged
            manually”. Use this when they went out by post or from your own inbox. This cannot be
            undone.
          </>
        }
        confirmLabel={`Issue ${drafts.length}`}
        onConfirm={async () => {
          const rows = await fetchInvoiceDrafts(drafts.map((d) => d.id));
          await recordAll(rows, 'manual');
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Bulk payment — a month of deposits, each invoice paid in full
// ---------------------------------------------------------------------------

/**
 * Why selected rows are not in the payment run, one line per reason. Grouped,
 * because "select all" on the Open tab also ticks every draft, and twenty-three
 * "#26-0011 (not sent)" in a row is a wall nobody reads.
 */
function leftAloneGroups(rows: InvoiceListItem[]): Array<{ key: string; label: string; numbers: string[] }> {
  const groups = [
    { key: 'draft', one: 'draft', many: 'drafts', why: 'not sent, so nobody owes them yet', test: (r: InvoiceListItem) => r.state === 'DRAFT' },
    { key: 'paid', one: 'paid invoice', many: 'paid invoices', why: 'nothing left to pay', test: (r: InvoiceListItem) => r.state === 'PAID' },
    { key: 'void', one: 'void invoice', many: 'void invoices', why: 'nothing is owed', test: (r: InvoiceListItem) => r.state === 'VOID' },
  ];
  const out: Array<{ key: string; label: string; numbers: string[] }> = [];
  const placed = new Set<string>();
  for (const g of groups) {
    const hit = rows.filter(g.test);
    hit.forEach((r) => placed.add(r.id));
    if (hit.length) out.push({ key: g.key, label: `${plural(hit.length, g.one, g.many)} — ${g.why}`, numbers: hit.map((r) => r.number) });
  }
  const rest = rows.filter((r) => !placed.has(r.id));
  if (rest.length) out.push({ key: 'rest', label: `${plural(rest.length, 'invoice', 'invoices')} with nothing due`, numbers: rest.map((r) => r.number) });
  return out;
}

/** "#1151, #1154, #26-0007 +20 more" — enough to recognise, never a wall. */
function numberList(numbers: string[], show = 4): string {
  const head = numbers.slice(0, show).map((n) => `#${n}`).join(', ');
  return numbers.length > show ? `${head} +${numbers.length - show} more` : head;
}

const receiptWent = (r: BulkPaymentResult['results'][number]) =>
  r.receipt?.status === 'sent' || r.receipt?.status === 'filed';

/**
 * Record a selection as PAID IN FULL. The rows are frozen when the dialog opens
 * and each carries the balance shown here to the server, which skips (by name)
 * any invoice whose balance moved in the meantime rather than pay a figure
 * nobody looked at. A part payment is recorded on the invoice itself.
 *
 * Payments are reversible — each one can be removed on its invoice — so this
 * is a plain confirmation, not the typed-count gate a permanent delete has.
 */
function BulkPaymentDialog({
  rows,
  left,
  onClose,
  onDone,
}: {
  rows: InvoiceListItem[];
  /** Selected rows not in the run — listed, never silently dropped. */
  left: InvoiceListItem[];
  onClose: () => void;
  onDone: () => void;
}) {
  const record = useRecordPayments();
  const [date, setDate] = useState(todayIso());
  const [method, setMethod] = useState(() => lastUsedMethod());
  const [notes, setNotes] = useState('');
  // Starts ticked, like every payment recorded by hand.
  const [sendReceipts, setSendReceipts] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Set once the run returns with something that did not land — the dialog
   *  then becomes the report, so a refusal is read, not toasted and lost. */
  const [report, setReport] = useState<BulkPaymentResult | null>(null);

  const total = rows.reduce((s, r) => s + r.dueAmount, 0);
  const dateError = !date
    ? 'Enter the date the money landed.'
    : date > todayIso()
      ? 'A payment cannot be dated in the future.'
      : null;

  const close = (next: boolean) => {
    if (next || record.isPending) return;
    if (report) onDone();
    else onClose();
  };

  const submit = async () => {
    if (dateError) return;
    setError(null);
    try {
      const res = await record.mutateAsync({
        items: rows.map((r) => ({ id: r.id, expectedDue: r.dueAmount })),
        date,
        method: method || undefined,
        notes: notes.trim() || undefined,
        sendReceipts,
      });
      rememberMethod(method);
      if (res.recorded > 0) {
        const receipts = res.results.filter(receiptWent).length;
        toast.success(
          `${plural(res.recorded, 'invoice', 'invoices')} paid · ${formatMoney(res.amount, { whole: true })}` +
            (sendReceipts ? ` · ${plural(receipts, 'receipt', 'receipts')} sent` : ''),
        );
      }
      const receiptGap =
        sendReceipts && res.results.some((r) => r.outcome === 'recorded' && !receiptWent(r));
      if (res.skipped + res.failed === 0 && !receiptGap) onDone();
      else setReport(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The payments could not be recorded.');
    }
  };

  if (report) {
    const notLanded = report.results.filter((r) => r.outcome !== 'recorded');
    const receiptGaps = report.results.filter(
      (r) => r.outcome === 'recorded' && r.receipt && !receiptWent(r),
    );
    return (
      <Modal
        open
        onOpenChange={close}
        title={
          report.recorded > 0
            ? `${plural(report.recorded, 'payment', 'payments')} recorded · ${formatMoney(report.amount, { whole: true })}`
            : 'No payments were recorded'
        }
        description="These did not go as asked. Everything else in the run is paid."
        footer={<Button onClick={onDone}>Done</Button>}
      >
        <ul className="max-h-72 divide-y divide-grey-100 overflow-y-auto rounded-md border border-grey-200 text-body-sm">
          {notLanded.map((r) => (
            <li key={r.id} className="px-3 py-2">
              <p className="text-secondary">
                {r.number ? <span className="num font-semibold">#{r.number}</span> : 'An invoice'}
                {r.client && <> · {r.client}</>}
                <span className="ml-2 text-caption font-semibold uppercase text-amber-700">
                  {r.outcome === 'failed' ? 'refused' : 'skipped'}
                </span>
              </p>
              <p className="text-caption text-grey-600">{r.reason}</p>
            </li>
          ))}
          {receiptGaps.map((r) => (
            <li key={`${r.id}:receipt`} className="px-3 py-2">
              <p className="text-secondary">
                <span className="num font-semibold">#{r.number}</span> · {r.client}
                <span className="ml-2 text-caption font-semibold uppercase text-amber-700">
                  paid · no receipt
                </span>
              </p>
              <p className="text-caption text-grey-600">
                The payment is recorded; the receipt was {r.receipt?.status}
                {r.receipt?.status === 'skipped' ? ' — the client has no billing email' : ''}. Re-send it
                from the invoice once that is fixed.
              </p>
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onOpenChange={close}
      title={`Record ${plural(rows.length, 'invoice', 'invoices')} as paid in full?`}
      description="Each is paid the balance shown. A part payment is recorded on the invoice itself, and any payment can be removed from its invoice."
      footer={
        <>
          <Button variant="outline" onClick={() => close(false)} disabled={record.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={record.isPending}
            disabled={Boolean(dateError)}
            title={dateError ?? undefined}
          >
            <CreditCard className="h-4 w-4" aria-hidden />
            Record {rows.length} · {formatMoney(total, { whole: true })}
          </Button>
        </>
      }
    >
      <ul className="mb-2 max-h-56 divide-y divide-grey-100 overflow-y-auto rounded-md border border-grey-200 text-body-sm">
        {rows.map((r) => (
          <li key={r.id} className="flex items-baseline justify-between gap-3 px-3 py-1.5">
            <span className="min-w-0 truncate text-secondary">
              <span className="num font-semibold">#{r.number}</span> · {r.client.name}
            </span>
            {/* One invoice's balance is a document figure — it keeps its cents. */}
            <span className="num shrink-0 text-grey-600">{formatMoney(r.dueAmount)}</span>
          </li>
        ))}
      </ul>
      <p className="num mb-4 flex justify-between px-3 text-body-sm font-semibold text-secondary">
        <span>{plural(rows.length, 'payment', 'payments')}</span>
        <span>{formatMoney(total, { whole: true })}</span>
      </p>
      {left.length > 0 && (
        <div className="mb-4 text-caption text-grey-500">
          <p className="font-semibold text-grey-600">Left alone</p>
          <ul>
            {leftAloneGroups(left).map((g) => (
              <li key={g.key}>
                {g.label} <span className="num text-grey-400">· {numberList(g.numbers)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Date received"
          type="date"
          max={todayIso()}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          error={dateError ?? undefined}
        />
        <Select
          label="Method"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          options={[
            { value: '', label: 'Not specified' },
            ...Object.entries(METHOD_LABELS).map(([value, label]) => ({ value, label })),
          ]}
        />
        <div className="sm:col-span-2">
          <Input
            label="Notes"
            placeholder="Deposit Oct 1, check batch…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            hint="Written on every payment in this run."
          />
        </div>
      </div>
      <SendReceiptField className="mt-4" many checked={sendReceipts} onChange={setSendReceipts} />
      {error && <p className="mt-3 text-body-sm font-medium text-error">{error}</p>}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Bulk delete — the count is typed back before anything can be pressed
// ---------------------------------------------------------------------------

/**
 * PERMANENTLY delete a selection of drafts. The rows are frozen when the dialog
 * opens (a refetch must not change what "14" refers to), every one is listed
 * with its amount, and the button stays disabled until the number of invoices
 * is typed back — the server checks the same number, so a keyboard reflex or a
 * stale tab cannot delete more than the person counted.
 */
function DeleteDraftsDialog({
  rows,
  skipped,
  onClose,
  onDeleted,
}: {
  rows: InvoiceListItem[];
  /** Selected rows left out: issued, paid, void, or owned by Harvest. */
  skipped: number;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const del = useDeleteInvoices();
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const count = rows.length;
  const matches = typed.trim() !== '' && Number(typed.trim()) === count;
  const total = rows.reduce((s, r) => s + r.amount, 0);

  const close = (next: boolean) => {
    if (!next && !del.isPending) onClose();
  };

  const submit = async () => {
    if (!matches) return;
    setError(null);
    try {
      const res = await del.mutateAsync({ ids: rows.map((r) => r.id), confirmCount: count });
      toast.success(`${plural(res.deleted, 'draft invoice', 'draft invoices')} deleted`);
      onDeleted();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The drafts could not be deleted.');
    }
  };

  return (
    <Modal
      open
      onOpenChange={close}
      title={`Delete ${plural(count, 'draft invoice', 'draft invoices')} permanently?`}
      description="This cannot be undone. Any time or expenses on them return to unbilled."
      footer={
        <>
          <Button variant="outline" onClick={() => close(false)} disabled={del.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void submit()} disabled={!matches} loading={del.isPending}>
            <Trash2 className="h-4 w-4" aria-hidden />
            Delete {count}
          </Button>
        </>
      }
    >
      <ul className="mb-2 max-h-64 divide-y divide-grey-100 overflow-y-auto rounded-md border border-grey-200 text-body-sm">
        {rows.map((r) => (
          <li key={r.id} className="flex items-baseline justify-between gap-3 px-3 py-1.5">
            <span className="min-w-0 truncate text-secondary">
              <span className="num font-semibold">#{r.number}</span> · {r.client.name}
            </span>
            <span className="num shrink-0 text-grey-500">{formatMoney(r.amount)}</span>
          </li>
        ))}
      </ul>
      <p className="num mb-4 flex justify-between px-3 text-body-sm font-semibold text-secondary">
        <span>{plural(count, 'draft', 'drafts')}</span>
        <span>{formatMoney(total, { whole: true })}</span>
      </p>
      {skipped > 0 && (
        <p className="mb-4 text-caption text-grey-500">
          {plural(skipped, 'other selected invoice is', 'other selected invoices are')} left alone —
          issued invoices are voided, never deleted, and drafts mirrored from Harvest are deleted in
          Harvest.
        </p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Input
          label={`Type ${count} to confirm`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          inputMode="numeric"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          error={error ?? undefined}
        />
      </form>
    </Modal>
  );
}
